import type { CompetitorProfile } from './CompetitorComparisonPage.tsx';

/**
 * Competitor profile data. Edit ONLY this file when prices change or a competitor pivots.
 * All claims must be sourced from the competitor's public pages or regulator filings.
 *
 * Pricing snapshots dated 2026-05-26. Set a calendar reminder every quarter to refresh.
 */

export const ACCESSIBE: CompetitorProfile = {
  name: 'AccessiBe',
  slug: 'accessibe',
  url: 'https://accessibe.com',
  tagline:
    'AccessiBe sells a JavaScript overlay widget that claims to auto-fix accessibility. EAA Proof gives you a real WCAG audit + a publishable accessibility statement instead.',
  intro:
    "AccessiBe is the highest-profile overlay vendor — you've probably seen their pricing start at around $49/month for small sites. The pitch: drop in their JavaScript snippet, an AI continuously adjusts your site, and you get a statement to display. It's an attractive idea for non-technical buyers who want a quick option without the engineering work.\n\nThe main consideration is regulatory. The US Department of Justice has stated that overlays alone do not ensure compliance, and the European Disability Forum's published position is that overlays do not, on their own, satisfy WCAG and the EAA. In the US, courts have allowed accessibility lawsuits to proceed against sites that used overlay widgets — the widget did not by itself resolve the claims.\n\nEAA Proof takes a different approach: we scan your actual code, produce an audit report your engineers can act on, and generate a draft accessibility statement that documents the fixes you make. The deliverable is the kind of documentation an accessibility review looks for, and it's something your developers can use to ship durable improvements.",
  weWinAt: [
    'You want a real audit report that regulators recognise — not a widget certificate',
    'You have a developer who can apply fixes (even one part-time)',
    'You need an EU model accessibility statement (Implementing Decision (EU) 2018/1523)',
    'You prefer a €29 one-time scan to a $49/month perpetual subscription',
    'You want a documented audit trail rather than relying on an overlay-only approach',
  ],
  theyWinAt: [
    'You have zero engineering capacity and want a one-line script to deploy',
    "You're comfortable relying on an overlay-only approach",
  ],
  rows: [
    {
      dimension: 'Pricing model',
      ours: { text: '€29 one-time scan, or €9/mo for 5 scans, or €49/mo agency plan', pro: true },
      theirs: { text: 'From $49/month subscription, billed annually (~$588/year)' },
    },
    {
      dimension: 'What you get',
      ours: {
        text: 'Downloadable PDF audit + EN 301 549-shaped accessibility statement HTML',
        pro: true,
      },
      theirs: { text: 'JavaScript widget injected into your site + a compliance certificate' },
    },
    {
      dimension: 'How it works',
      ours: { text: 'Real Chromium browser scan with axe-core 4.10, no widget on your site' },
      theirs: { text: 'Persistent JavaScript overlay loaded by every visitor', con: true },
    },
    {
      dimension: 'Effect on page load',
      ours: { text: 'Zero — nothing added to your site', pro: true },
      theirs: { text: '~80–200 KB of widget JS loaded on every page', con: true },
    },
    {
      dimension: 'Effect on screen-reader users',
      ours: { text: 'No effect; fixes are applied to your real code by your developers' },
      theirs: {
        text: 'Documented interference in NVDA / JAWS / VoiceOver per WebAIM accessibility community testing',
        con: true,
      },
    },
    {
      dimension: 'Regulator acceptance',
      ours: { text: 'EU model accessibility statement, ready to publish', pro: true },
      theirs: {
        text: 'US DOJ has stated overlays alone do not ensure compliance (2022); EU guidance still developing',
        con: true,
      },
    },
    {
      dimension: 'Multi-page crawl',
      ours: { text: 'Up to 10 same-origin pages per scan', pro: true },
      theirs: { text: 'N/A — widget runs per page in the visitor browser' },
    },
    {
      dimension: 'Remediation guidance',
      ours: {
        text: 'Plain-English fixes grounded in your actual HTML, by Claude Sonnet 4.6',
        pro: true,
      },
      theirs: { text: 'None for your developers — fixes happen client-side in the widget' },
    },
    {
      dimension: 'B2B VAT-compliant invoicing',
      ours: { text: 'Yes — automatic via Stripe Tax', pro: true },
      theirs: { text: 'Yes' },
    },
    {
      dimension: 'Cancel anytime',
      ours: { text: 'No subscription for single scans; monthly plans cancel anytime', pro: true },
      theirs: { text: 'Annual commitment is standard pricing', con: true },
    },
    {
      dimension: 'Free trial / sample',
      ours: { text: 'Live sample report viewable at /sample — no sign-up needed', pro: true },
      theirs: { text: 'Free 7-day trial after card capture' },
    },
  ],
};

export const USERWAY: CompetitorProfile = {
  name: 'UserWay',
  slug: 'userway',
  url: 'https://userway.org',
  tagline:
    'UserWay is the other major overlay-widget vendor. EAA Proof produces a documented audit report instead of a client-side script.',
  intro:
    "UserWay is structurally similar to AccessiBe — a JavaScript widget you drop on your site, marketed to non-technical buyers who want a checkbox solution. They've achieved significant scale through aggressive freemium positioning (a free tier exists that adds a small accessibility button to your site) and pricing for the paid tiers starts around $49/month.\n\nThe regulatory critique that applies to AccessiBe applies equally to UserWay. WebAIM, the W3C, and the European Disability Forum have all published positions that overlays cannot remediate accessibility failures and may introduce new ones. The same DOJ guidance and EU regulatory stance affects both vendors.\n\nEAA Proof's positioning vs UserWay is identical to our positioning vs AccessiBe: we sell the audit + statement deliverable that satisfies what the EAA actually requires, instead of a runtime script that papers over violations.",
  weWinAt: [
    'You want an actual WCAG 2.1 AA audit report, not a runtime widget',
    'You need to publish an EU model accessibility statement (Implementing Decision (EU) 2018/1523)',
    'You prefer one-time €29 scans to recurring monthly subscriptions',
    'You want fixes your developers can actually apply',
  ],
  theyWinAt: [
    'You want a free tier that adds an accessibility toolbar to your site',
    'You specifically want a widget-based approach',
  ],
  rows: [
    {
      dimension: 'Pricing model',
      ours: { text: '€29 one-time scan, or €9/mo for 5 scans, or €49/mo agency plan', pro: true },
      theirs: { text: 'Free tier + paid tiers from $49/month' },
    },
    {
      dimension: 'What you get',
      ours: {
        text: 'Downloadable PDF audit + EN 301 549-shaped accessibility statement HTML',
        pro: true,
      },
      theirs: { text: 'JavaScript accessibility toolbar injected into your site' },
    },
    {
      dimension: 'How it works',
      ours: { text: 'Real Chromium browser scan with axe-core 4.10, no widget on your site' },
      theirs: { text: 'Persistent JavaScript widget loaded on every page', con: true },
    },
    {
      dimension: 'Effect on page load',
      ours: { text: 'Zero — nothing added to your site', pro: true },
      theirs: { text: '~100+ KB of widget JS loaded on every page', con: true },
    },
    {
      dimension: 'Regulator acceptance',
      ours: { text: 'EU model accessibility statement, ready to publish', pro: true },
      theirs: { text: 'Same overlay critique as AccessiBe applies (DOJ, EDF, WebAIM)', con: true },
    },
    {
      dimension: 'Generates accessibility statement',
      ours: { text: 'Yes — auto-filled, ready to publish at /accessibility', pro: true },
      theirs: { text: 'A widget UI only; no published statement document' },
    },
    {
      dimension: 'Multi-page crawl',
      ours: { text: 'Up to 10 same-origin pages per scan', pro: true },
      theirs: { text: 'N/A — widget runs per page in the visitor browser' },
    },
    {
      dimension: 'Remediation guidance for engineers',
      ours: {
        text: 'Plain-English fixes grounded in your actual HTML, by Claude Sonnet 4.6',
        pro: true,
      },
      theirs: { text: 'Limited code-side recommendations; widget does the "fixing"' },
    },
    {
      dimension: 'Agency / multi-site offering',
      ours: { text: '€49/mo agency plan: 50 scans + your branding on the PDF reports', pro: true },
      theirs: { text: 'Multi-site licensing exists; pricing on request' },
    },
    {
      dimension: 'B2B VAT-compliant EU invoicing',
      ours: { text: 'Yes — automatic via Stripe Tax', pro: true },
      theirs: { text: 'Yes' },
    },
  ],
};

export const SITEIMPROVE: CompetitorProfile = {
  name: 'Siteimprove',
  slug: 'siteimprove',
  url: 'https://www.siteimprove.com',
  tagline:
    'Siteimprove is the enterprise web-quality platform. EAA Proof is the right fit for SMBs and agencies who need a documented, standards-based audit without a six-figure contract.',
  intro:
    "Siteimprove is the established enterprise platform in this space. It covers accessibility, SEO, content quality, policy, and analytics in one product. Pricing is non-public — sales-led with annual contracts typically in the €10,000+/year range for the accessibility module alone, scaled by pages crawled.\n\nIf you're a large organisation that already has a procurement process, an accessibility team, and a multi-month rollout budget, Siteimprove is a good fit. Their platform integrates deeply with enterprise workflows and they have direct relationships with EU governmental bodies.\n\nEAA Proof is built for everyone else — the SMB e-commerce site that needs an EAA-compliant statement now, the freelance agency that audits five client sites a month, the SaaS startup that just got an investor pre-deal accessibility request. We give you the same standards-conformant audit output (axe-core + EN 301 549) without the procurement cycle, the annual commitment, or the four-figure-a-month price.",
  weWinAt: [
    "You're an SMB, freelancer, or agency — not a Fortune 500 procurement process",
    'You need an audit + statement today, not after a sales call and a 6-week PoC',
    'You want transparent self-serve pricing (€29 / €9 mo / €49 mo) without a quote',
    'You only need accessibility — not SEO, content quality, policy management, and analytics bundled in',
  ],
  theyWinAt: [
    'You need an integrated platform that also handles SEO, content quality, policy, and analytics',
    'You have a dedicated accessibility team and a procurement budget for enterprise tooling',
    'You require integrations with enterprise systems (SharePoint, Adobe AEM, Sitecore, etc.)',
  ],
  rows: [
    {
      dimension: 'Pricing transparency',
      ours: { text: 'Listed publicly: €29 / €9 mo / €49 mo', pro: true },
      theirs: { text: 'Sales-led; pricing on request, typically €10k+/year', con: true },
    },
    {
      dimension: 'Time to first audit',
      ours: { text: 'Sign in, drop URL, audit ready in 60–120 seconds', pro: true },
      theirs: { text: 'Discovery call → demo → PoC → contract → onboarding (weeks)', con: true },
    },
    {
      dimension: 'Annual commitment',
      ours: { text: 'None — single scans are one-time, plans cancel anytime', pro: true },
      theirs: { text: 'Annual contract is standard', con: true },
    },
    {
      dimension: 'Accessibility scanner engine',
      ours: { text: 'axe-core 4.10 + Microsoft Playwright (real Chromium)' },
      theirs: { text: 'Proprietary scanner with WCAG mapping' },
    },
    {
      dimension: 'Compliance statement output',
      ours: { text: 'EU model accessibility statement HTML, auto-filled and ready to publish', pro: true },
      theirs: { text: 'Workflow tooling to manage statements; output format varies' },
    },
    {
      dimension: 'Multi-page crawl',
      ours: { text: 'Up to 10 same-origin pages per scan' },
      theirs: { text: 'Unlimited pages, scaled by contracted volume' },
    },
    {
      dimension: 'Multi-site / agency workflows',
      ours: { text: '€49/mo agency plan: 50 audits/month with your branding on PDFs', pro: true },
      theirs: { text: 'Enterprise multi-site available at enterprise pricing' },
    },
    {
      dimension: 'Integrations',
      ours: { text: 'API + downloadable PDF/HTML; integrations on the roadmap' },
      theirs: {
        text: 'Deep integrations with enterprise CMS (AEM, Sitecore, SharePoint)',
      },
    },
    {
      dimension: 'AI remediation guidance',
      ours: { text: 'Claude Sonnet 4.6 plain-English fixes, grounded in your HTML', pro: true },
      theirs: { text: 'Issue descriptions with severity; no AI-generated fix suggestions' },
    },
    {
      dimension: 'Best fit for',
      ours: { text: 'SMBs, agencies, freelancers, startup compliance', pro: true },
      theirs: { text: 'Enterprise teams with procurement budget and platform scope' },
    },
  ],
};
